import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import Table from '../components/ui/Table';
import PageHeader from '../components/shared/PageHeader';
import { currency, date } from '../utils/formatters';
import { normalizeMoney } from '../utils/masks';
import { supabase } from '../lib/supabase';
import { getOfficeContext } from '../lib/office';

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const paymentLabel = (value) => {
  const key = normalizeText(value).replace(/\s+/g, '_');
  const labels = {
    pix: 'PIX',
    dinheiro: 'Dinheiro',
    cartao: 'Cartão',
    cartao_credito: 'Cartão de crédito',
    cartao_debito: 'Cartão de débito',
    boleto: 'Boleto',
    fiado: 'Fiado',
    misto: 'Misto',
    nao_informado: 'Não informado',
  };
  return labels[key] || (String(value || '').trim() || 'Não informado');
};

export default function Financeiro() {
  const [tipo, setTipo] = useState('');
  const [allRows, setAllRows] = useState([]);
  const [officeId, setOfficeId] = useState(null);
  const [desc, setDesc] = useState('');
  const [val, setVal] = useState('');
  const [forma, setForma] = useState('pix');
  const [kind, setKind] = useState('receita');
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = async () => {
    const { officeId: resolvedOfficeId } = await getOfficeContext();
    if (!resolvedOfficeId) {
      toast.error('Não foi possível identificar a oficina.');
      return;
    }

    setOfficeId(resolvedOfficeId);

    const { data, error } = await supabase
      .from('financeiro')
      .select('*')
      .eq('oficina_id', resolvedOfficeId)
      .order('data', { ascending: false });

    if (error) {
      toast.error(error.message || 'Erro ao carregar financeiro.');
      return;
    }

    setAllRows(data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const rows = (allRows || []).filter((f) => !tipo || f.tipo === tipo);
  const receita = rows.filter((r) => r.tipo === 'receita').reduce((a, b) => a + Number(b.valor || 0), 0);
  const desp = rows.filter((r) => r.tipo === 'despesa').reduce((a, b) => a + Number(b.valor || 0), 0);
  const lucro = receita - desp;
  const ticket = receita / Math.max(1, rows.filter((r) => r.tipo === 'receita').length);

  const byMonth = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const d = new Date(r.data || Date.now());
      const k = `${d.getMonth() + 1}/${d.getFullYear().toString().slice(-2)}`;
      if (!map[k]) map[k] = { mes: k, receita: 0, despesa: 0 };
      map[k][r.tipo === 'receita' ? 'receita' : 'despesa'] += Number(r.valor || 0);
    });
    return Object.values(map).slice(-6);
  }, [rows]);

  const formas = useMemo(() => {
    const totals = new Map();
    rows.forEach((row) => {
      const label = paymentLabel(row.forma_pagamento);
      totals.set(label, (totals.get(label) || 0) + Number(row.valor || 0));
    });
    return Array.from(totals, ([f, total]) => ({ f, total })).filter((item) => item.total > 0);
  }, [rows]);

  const openNewLancamento = () => {
    setFeedback(null);
    setShowForm(true);
  };

  const closeNewLancamento = () => {
    if (saving) return;
    setFeedback(null);
    setShowForm(false);
  };

  const addLanc = async () => {
    if (saving) return;

    setFeedback(null);
    const descricao = desc.trim() || (kind === 'receita' ? 'Receita manual' : 'Despesa manual');
    const valor = normalizeMoney(val);

    if (!Number.isFinite(valor) || valor <= 0) {
      setFeedback({ type: 'error', message: 'Informe um valor maior que zero.' });
      toast.error('Informe um valor maior que zero.');
      return;
    }

    try {
      setSaving(true);
      const resolvedOfficeId = officeId || (await getOfficeContext()).officeId;

      if (!resolvedOfficeId) {
        toast.error('Não foi possível identificar a oficina.');
        return;
      }

      const { error } = await supabase.from('financeiro').insert({
        oficina_id: resolvedOfficeId,
        tipo: kind,
        descricao,
        valor,
        forma_pagamento: forma,
        data: new Date().toISOString(),
      });

      if (error) throw error;

      toast.success('Lançamento adicionado com sucesso.');
      setDesc('');
      setVal('');
      await load();
      setShowForm(false);
    } catch (error) {
      console.error('Erro ao adicionar lançamento:', error);
      setFeedback({ type: 'error', message: error.message || 'Erro ao adicionar lançamento.' });
      toast.error(error.message || 'Erro ao adicionar lançamento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Financeiro"
        subtitle="Receitas, despesas e rentabilidade"
        actions={<Button onClick={openNewLancamento}>Adicionar lançamento</Button>}
      />

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Card><p className="text-sm text-zinc-400">Receita total</p><p className="font-display text-3xl text-success">{currency(receita)}</p></Card>
        <Card><p className="text-sm text-zinc-400">Despesas</p><p className="font-display text-3xl text-danger">{currency(desp)}</p></Card>
        <Card><p className="text-sm text-zinc-400">Lucro líquido</p><p className="font-display text-3xl text-info">{currency(lucro)}</p></Card>
        <Card><p className="text-sm text-zinc-400">Ticket médio</p><p className="font-display text-3xl text-accent">{currency(ticket)}</p></Card>
      </div>

      <Card>
        <div className="grid gap-3 md:grid-cols-2">
          <Select value={tipo} onChange={(e) => setTipo(e.target.value)} options={[{ value: '', label: 'Filtro tipo' }, { value: 'receita', label: 'Receita' }, { value: 'despesa', label: 'Despesa' }]} />
          <Button variant="ghost" onClick={openNewLancamento}>Novo lançamento</Button>
        </div>
      </Card>

      <Modal open={showForm} onClose={closeNewLancamento} title="Novo lançamento">
        <div className="space-y-3">
          {feedback && (
            <div className={`rounded-lg border px-3 py-2 text-sm ${
              feedback.type === 'success'
                ? 'border-green-500/30 bg-green-500/10 text-green-300'
                : 'border-red-500/30 bg-red-500/10 text-red-300'
            }`}>
              {feedback.message}
            </div>
          )}
          <Input placeholder="Descrição" value={desc} onChange={(e) => setDesc(e.target.value)} />
          <Input placeholder="Valor (R$)" mask="money" value={val} onChange={(e) => setVal(e.target.value)} />
          <Select value={kind} onChange={(e) => setKind(e.target.value)} options={[{ value: 'receita', label: 'Receita' }, { value: 'despesa', label: 'Despesa' }]} />
          <Select value={forma} onChange={(e) => setForma(e.target.value)} options={[{ value: 'pix', label: 'PIX' }, { value: 'dinheiro', label: 'Dinheiro' }, { value: 'cartao', label: 'Cartão' }, { value: 'fiado', label: 'Fiado' }]} />
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" disabled={saving} onClick={closeNewLancamento}>Cancelar</Button>
            <Button disabled={saving} onClick={addLanc}>{saving ? 'Salvando...' : 'Salvar lançamento'}</Button>
          </div>
        </div>
      </Modal>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={byMonth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a30" />
                <XAxis dataKey="mes" stroke="#72727a" />
                <YAxis stroke="#72727a" />
                <Tooltip />
                <Bar dataKey="receita" fill="#22c55e" />
                <Bar dataKey="despesa" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <h3 className="mb-2 font-semibold">Formas de pagamento</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={formas} dataKey="total" nameKey="f" outerRadius={90}>
                  {formas.map((_, i) => <Cell key={i} fill={['#22c55e', '#f97316', '#3b82f6', '#eab308', '#8b5cf6', '#14b8a6', '#f43f5e'][i % 7]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Table
        columns={['Tipo', 'Descrição', 'Valor', 'Data', 'Forma']}
        rows={rows}
        renderRow={(r) => (
          <tr key={r.id} className="border-t border-border hover:bg-surface2">
            <td className="px-3 py-2">{r.os_id ? 'receita OS' : r.tipo}</td>
            <td className="px-3 py-2">{r.descricao}</td>
            <td className="px-3 py-2">{currency(r.valor)}</td>
            <td className="px-3 py-2">{date(r.data)}</td>
            <td className="px-3 py-2">{paymentLabel(r.forma_pagamento)}</td>
          </tr>
        )}
      />

      <Card>
        <h3 className="mb-2 font-semibold">Resumo por forma de pagamento</h3>
        <div className="grid gap-2 md:grid-cols-4">
          {formas.map((i) => (
            <div key={i.f} className="rounded-lg bg-surface2 p-2">
              <p className="text-xs text-zinc-400">{i.f}</p>
              <p className="font-semibold">{currency(i.total)}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
